import React, {useState} from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  ProgressBar,
  Alert,
  Card,
} from "react-bootstrap";
import axios from "axios";
import "./BookingModal.css";

const BookingModal = ({
  show,
  onHide,
  selectedPortofLoading,
  selectedPortofDischarge,
  selectedDate,
  selectedReturnDate,
  isRoundTrip,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Fahrzeugdaten
    length: "",
    width: "",
    height: "",
    weight: "",
    // Kontaktdaten
    name: "",
    company: "",
    email: "",
    phone: "",
    additionalQuestions: "",
    // Bestätigungen
    acceptTerms: false,
    confirmBusinessInquiry: false,
  });

  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({
    type: null, // "success" | "error"
    message: "",
  });

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Fehler für dieses Feld entfernen
    if (errors[name]) {
      setErrors((prev) => ({...prev, [name]: null}));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.length || parseFloat(formData.length) <= 0) {
        newErrors.length = "Bitte geben Sie eine gültige Länge ein";
      }
      if (!formData.width || parseFloat(formData.width) <= 0) {
        newErrors.width = "Bitte geben Sie eine gültige Breite ein";
      }
      if (!formData.height || parseFloat(formData.height) <= 0) {
        newErrors.height = "Bitte geben Sie eine gültige Höhe ein";
      }
      if (!formData.weight || parseFloat(formData.weight) <= 0) {
        newErrors.weight = "Bitte geben Sie ein gültiges Gewicht ein";
      }
    }

    if (step === 2) {
      if (!formData.name.trim()) {
        newErrors.name = "Bitte geben Sie Ihren Namen ein";
      }
      if (!formData.company.trim()) {
        newErrors.company = "Bitte geben Sie Ihre Firma ein";
      }
      if (!formData.email.trim()) {
        newErrors.email = "Bitte geben Sie Ihre E-Mail ein";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Bitte geben Sie eine gültige E-Mail ein";
      }
    }

    if (step === 3) {
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = "Bitte akzeptieren Sie die AGB";
      }
      if (!formData.confirmBusinessInquiry) {
        newErrors.confirmBusinessInquiry =
          "Bitte bestätigen Sie die Unternehmensanfrage";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (validateStep(3)) {
      try {
        setSubmitStatus({type: null, message: ""});

        const emailData = {
          name: formData.name,
          company: formData.company || "",
          email: formData.email,
          phone: formData.phone || "",
          length: formData.length || "",
          width: formData.width || "",
          height: formData.height || "",
          weight: formData.weight || "",
          additionalQuestions: formData.additionalQuestions || "",
          selectedPortofLoading: selectedPortofLoading?.name || "",
          selectedPortofDischarge: selectedPortofDischarge?.name || "",
          selectedDate: selectedDate ? selectedDate.toISOString() : "",
          selectedReturnDate: selectedReturnDate
            ? selectedReturnDate.toISOString()
            : "",
          isRoundTrip: !!isRoundTrip,
          internalEmail: "booking@jh-ferry.de",
        };

        await axios.post(
          "https://mmm-email-server.osc-fr1.scalingo.io/api/jhferry/BookingViaWebsite",
          emailData,
          {
            headers: {
              "x-api-key": process.env.REACT_APP_EMAIL_SERVER_API_KEY,
            },
          },
        );

        setSubmitStatus({
          type: "success",
          message: "Buchungsanfrage erfolgreich gesendet.",
        });

        setTimeout(() => {
          onHide();
          setCurrentStep(1);
          setFormData({
            length: "",
            width: "",
            height: "",
            weight: "",
            name: "",
            company: "",
            email: "",
            phone: "",
            additionalQuestions: "",
            acceptTerms: false,
            confirmBusinessInquiry: false,
          });
          setSubmitStatus({type: null, message: ""});
        }, 1200);
      } catch (error) {
        setSubmitStatus({
          type: "error",
          message:
            "Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        });
        console.error("Fehler beim Senden der Buchungsanfrage:", error);
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("de-DE");
  };

  const renderStepIndicator = () => {
    const steps = [
      {number: 1, title: "Fahrzeug", icon: "🚛"},
      {number: 2, title: "Kontakt", icon: "👤"},
      {number: 3, title: "Bestätigung", icon: "✓"},
    ];

    return (
      <div className="step-indicator">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div
              className={`step-item ${
                currentStep === step.number ? "active" : ""
              } ${currentStep > step.number ? "completed" : ""}`}
            >
              <div className="step-circle">
                <span className="step-icon">{step.icon}</span>
              </div>
              <span className="step-title">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`step-line ${
                  currentStep > step.number ? "completed" : ""
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <h4>🚛 Fahrzeugdaten</h4>
              <p className="text-muted">
                Geben Sie die Maße Ihres Fahrzeugs ein
              </p>
            </div>

            <Card className="data-card">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Länge (m) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="length"
                        value={formData.length}
                        onChange={handleInputChange}
                        isInvalid={!!errors.length}
                        placeholder="z.B. 13.6"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.length}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Breite (m) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="width"
                        value={formData.width}
                        onChange={handleInputChange}
                        isInvalid={!!errors.width}
                        placeholder="z.B. 2.55"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.width}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Höhe (m) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="height"
                        value={formData.height}
                        onChange={handleInputChange}
                        isInvalid={!!errors.height}
                        placeholder="z.B. 4.0"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.height}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Gewicht (t) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        isInvalid={!!errors.weight}
                        placeholder="z.B. 24.0"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.weight}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <h4>👤 Kontaktdaten</h4>
              <p className="text-muted">Wie können wir Sie erreichen?</p>
            </div>

            <Card className="data-card">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        isInvalid={!!errors.name}
                        placeholder="Max Mustermann"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Firma <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        isInvalid={!!errors.company}
                        placeholder="Musterfirma GmbH"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.company}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        E-Mail <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        isInvalid={!!errors.email}
                        placeholder="max.mustermann@beispiel.de"
                        className="modern-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telefon (optional)</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+49 123 456789"
                        className="modern-input"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Weitere Fragen oder Anmerkungen</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="additionalQuestions"
                    value={formData.additionalQuestions}
                    onChange={handleInputChange}
                    placeholder="Hier können Sie zusätzliche Informationen oder Fragen eingeben..."
                    className="modern-input"
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div className="step-header">
              <h4>✓ Zusammenfassung & Bestätigung</h4>
              <p className="text-muted">Überprüfen Sie Ihre Angaben</p>
            </div>

            <Card className="summary-card route-info">
              <Card.Body>
                <h6 className="card-title">🗺️ Routendetails</h6>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Von</span>
                    <span className="info-value">
                      {selectedPortofLoading?.name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Nach</span>
                    <span className="info-value">
                      {selectedPortofDischarge?.name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Hinfahrt</span>
                    <span className="info-value">
                      {formatDate(selectedDate)}
                    </span>
                  </div>
                  {isRoundTrip && (
                    <div className="info-item">
                      <span className="info-label">Rückfahrt</span>
                      <span className="info-value">
                        {formatDate(selectedReturnDate)}
                      </span>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            <Card className="summary-card vehicle-info">
              <Card.Body>
                <h6 className="card-title">🚛 Fahrzeugdaten</h6>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Länge</span>
                    <span className="info-value">{formData.length} m</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Breite</span>
                    <span className="info-value">{formData.width} m</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Höhe</span>
                    <span className="info-value">{formData.height} m</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Gewicht</span>
                    <span className="info-value">{formData.weight} t</span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="summary-card contact-info">
              <Card.Body>
                <h6 className="card-title">👤 Kontaktdaten</h6>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Name</span>
                    <span className="info-value">{formData.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Firma</span>
                    <span className="info-value">{formData.company}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">E-Mail</span>
                    <span className="info-value">{formData.email}</span>
                  </div>
                  {formData.phone && (
                    <div className="info-item">
                      <span className="info-label">Telefon</span>
                      <span className="info-value">{formData.phone}</span>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            <Card className="confirmation-card">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleInputChange}
                    isInvalid={!!errors.acceptTerms}
                    label={
                      <span>
                        Ich akzeptiere die{" "}
                        <a
                          href="/agb"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          AGB
                        </a>{" "}
                        <span className="text-danger">*</span>
                      </span>
                    }
                    feedback={errors.acceptTerms}
                    feedbackType="invalid"
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Check
                    type="checkbox"
                    name="confirmBusinessInquiry"
                    checked={formData.confirmBusinessInquiry}
                    onChange={handleInputChange}
                    isInvalid={!!errors.confirmBusinessInquiry}
                    label={
                      <span>
                        Hiermit bestätige ich, dass es sich um eine
                        geschäftliche Anfrage handelt{" "}
                        <span className="text-danger">*</span>
                      </span>
                    }
                    feedback={errors.confirmBusinessInquiry}
                    feedbackType="invalid"
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      className="booking-modal"
    >
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title>⛴️ Buchungsanfrage</Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body-custom">
        {submitStatus.type && (
          <Alert
            variant={submitStatus.type === "success" ? "success" : "danger"}
            className="mb-3"
          >
            {submitStatus.message}
          </Alert>
        )}
        {renderStepIndicator()}
        <div className="progress-wrapper">
          <ProgressBar
            now={(currentStep / 3) * 100}
            className="modern-progress"
          />
        </div>
        {renderStepContent()}
      </Modal.Body>

      <Modal.Footer className="modal-footer-custom">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          className="btn-custom"
        >
          Abbrechen
        </Button>
        <div className="flex-grow-1"></div>
        {currentStep > 1 && (
          <Button
            variant="secondary"
            onClick={handleBack}
            className="btn-custom"
          >
            ← Zurück
          </Button>
        )}
        {currentStep < 3 ? (
          <Button
            variant="primary"
            onClick={handleNext}
            className="btn-custom btn-next"
          >
            Weiter →
          </Button>
        ) : (
          <Button
            variant="success"
            onClick={handleSubmit}
            className="btn-custom btn-submit"
          >
            ✓ Anfrage absenden
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BookingModal;
